import { useState } from 'react'
import { PRODUCT_NAME } from '../brand.js'
import { InboxShell } from '../inbox/InboxShell.js'
import { ContactRecord } from '../contacts/ContactRecord.js'
import { ContactsList } from '../contacts/ContactsList.js'
import { BookingsSection } from '../bookings/BookingsSection.js'
import { signOut, type CurrentUser } from './api.js'

/**
 * ⛔ A PLACEHOLDER. INBOX REPLACES THIS ENTIRELY.
 *
 * ┌─ WHY IT EXISTS AND WHAT IT DELIBERATELY IS NOT ────────────────────────────┐
 * │ It proves ONE thing: that a successful sign in produced a working session   │
 * │ the server recognises. It shows who you are, from `/auth/me`, and lets you  │
 * │ sign out. THAT IS THE WHOLE SCOPE.                                         │
 * │                                                                            │
 * │ No navigation, no layout, no inbox, no shell. Those are INBOX's, against    │
 * │ the information architecture from the §13 design session, and inventing     │
 * │ them here would pre-empt that work and produce something thrown away -      │
 * │ which is the reason `App.tsx` was a token scaffold in the first place.      │
 * │                                                                            │
 * │ ⚠️ BUILT 2026-07-30, AFTER THE FIRST REAL LOGIN SUCCEEDED AND APPEARED TO   │
 * │ DO NOTHING. `AuthFlow` called `onAuthenticated?.(to)` and `App.tsx` never   │
 * │ passed the prop, so four successful logins in three minutes each            │
 * │ authenticated, issued a session, and returned into `undefined?.()`. The     │
 * │ server logged `login succeeded` four times while the form sat there.        │
 * │                                                                            │
 * │ So the missing piece was never the login. It was that there was NOWHERE FOR │
 * │ A SIGNED-IN USER TO BE. This is the smallest honest somewhere.              │
 * └────────────────────────────────────────────────────────────────────────────┘
 */

type Props = {
  user: CurrentUser
  onSignedOut: () => void
  /**
   * Open the existing Two Factor Setup screen.
   *
   * REQUIRED, not optional, and that is the point of this whole file: an
   * optional callback here would reintroduce the exact defect above. See the
   * note in `AuthFlow.tsx`.
   */
  onSetUpTwoFactor: () => void
  /**
   * ⛔ WHICH SECTION IS OPEN. `null` is the inbox; a number is the contact
   * record for that reference. Threaded from `App.tsx` like every other route
   * value, for the reason below: a component that reads the URL itself is a
   * second owner of the route.
   */
  contactReference?: number | null
  /** ⛔ The contacts LIST, which is the section's index. Mutually exclusive with
   * `contactReference` in practice; App decides from the route. */
  showContactsList?: boolean
  /** ⛔ The board. CRM-BUILD item 28, and the product's THIRD top-level section. */
  showBoard?: boolean
  boardParams?: string
  onBoardParamsChange?: (next: URLSearchParams) => void
  onOpenBooking?: (reference: number) => void
  onOpenContact?: (reference: number) => void
  /** The list's sort, filter and view, as they appear in the URL. */
  listParams?: string
  onListParamsChange?: (next: URLSearchParams) => void
  /** Which top-level section is open, and how to change it. Both from `App.tsx`,
   * which owns the route in both directions. */
  onOpenSection?: (section: 'inbox' | 'contacts' | 'bookings') => void
  /**
   * The open conversation and how to change it. **Both come from `App.tsx`**,
   * which owns the route in both directions - see `web/src/routing.ts`.
   *
   * ⚠️ THREADED THROUGH RATHER THAN READ HERE, and that is deliberate for a file
   * whose header says INBOX replaces it. A component that reads the URL itself
   * is one more place the routing lives, and this one is scheduled for deletion.
   */
  conversationId: string | null
  /** ⛔ The message to scroll to and mark, from a search result. `Build_Order:490`. */
  highlightMessageId?: string | null
  onOpenConversation: (id: string) => void
}

export function SignedIn({
  user,
  onSignedOut,
  onSetUpTwoFactor,
  conversationId,
  highlightMessageId = null,
  onOpenConversation,
  contactReference = null,
  showContactsList = false,
  showBoard = false,
  boardParams = '',
  onBoardParamsChange,
  onOpenBooking,
  onOpenContact,
  onOpenSection,
  listParams = '',
  onListParamsChange,
}: Props): React.JSX.Element {
  const [busy, setBusy] = useState(false)
  const section = showBoard
    ? 'bookings'
    : showContactsList || contactReference !== null
      ? 'contacts'
      : 'inbox'

  return (
    /**
     * ⛔ NOT `.auth`, AND THE DIFFERENCE IS THE WHOLE SCROLL FIX.
     *
     * `.auth` is the SIGN-IN CARD layout: `min-height: 100vh`, flex, centred on
     * both axes. It exists to float one small card in the middle of an empty
     * page, and it is correct for every screen that does that.
     *
     * ⚠️ THE INBOX WAS MOUNTED INSIDE IT AS SCAFFOLDING and inherited a layout
     * designed to GROW around its content - so the conversation list could never
     * be bounded, and its `overflow-y: auto` was unreachable no matter what any
     * ancestor in `App.css` said. **Found by dumping the real ancestor chain
     * from a browser after a first attempt was made against `.shell`, which the
     * list is not inside.**
     */
    <div className="signed-in">
      {/*
        ⛔ THE SOCKET'S LIFETIME IS THIS COMPONENT'S, AND THAT IS THE POINT OF
        MOUNTING IT HERE RATHER THAN IN App.

        Signing out unmounts this branch, which unmounts the shell, which stops
        the socket. Mounted a level higher it would survive logout and keep
        reconnecting with a cookie the server has already destroyed - a 401 loop
        that reads as an authentication defect rather than as a lifetime one.

        ⚠️ IT RENDERED THE BANNER AND NOTHING ELSE UNTIL 2026-08-08. This block
        forbade a list, then a thread, on the grounds that building either to
        host a socket would arrive ahead of its own measurements. **The order
        held both times** - the list's query was measured and registered with the
        plan guard first, and the thread's quote lateral was bounded before
        anything rendered it - so the sentence is rewritten rather than deleted.
        A comment that forbids what the file now does is the stale argument
        corrected in `broadcast.ts`.
      */}
      {/*
        ┌─ ⛔ A SLIM BAR, BECAUSE THE PLACEHOLDER CARD WAS EATING 52% OF THE ────┐
        │ VIEWPORT AND EVERY LAYOUT TEST PASSED ANYWAY.                          │
        │                                                                       │
        │ Measured 2026-08-09 at 1280x800, on the real page: `.inbox-shell` was  │
        │ **333px** and `.auth__card` was **419px**. The card was a sibling, not │
        │ a replacement - the inbox had been added BESIDE the placeholder.       │
        │                                                                       │
        │ ⚠️ AND THE COMMENT ABOVE IT SAID "INBOX REPLACES IT", written by the   │
        │ person who left it mounted. A claim in the shape of a description, in  │
        │ the file that contradicted it.                                        │
        │                                                                       │
        │ ⛔ WHAT SURVIVES FROM THE CARD IS WHAT A SIGNED-IN PERSON NEEDS AND    │
        │ CANNOT GET ELSEWHERE: who they are, a way out, and TOTP enrolment -    │
        │ which needs a session, so this is still the only screen it is possible │
        │ from. The identity dump and the placeholder footnote go, because the   │
        │ inbox IS the application shell now and saying otherwise was the claim. │
        └───────────────────────────────────────────────────────────────────────┘
      */}
      <header className="app-bar">
        <span className="app-bar__brand">{PRODUCT_NAME}</span>
        {/**
         * ⛔ TWO SECTIONS, BECAUSE TWO EXIST. §13's nav table has seven, and
         * listing five that do not is the empty-card rule applied to navigation:
         * it says the feature is there and this deployment has none of it.
         *
         * ⚠️ AND THE LIST HAD NO WAY IN UNTIL THIS. A surface reachable only by
         * typing its URL is the `captureReturnTo` shape - built, correct, and
         * unreachable - which this project has met three times.
         */}
        {onOpenSection !== undefined && (
          <nav className="app-bar__nav" aria-label="Sections">
            <button
              type="button"
              className={section === 'inbox' ? 'app-bar__section app-bar__section--on' : 'app-bar__section'}
              aria-current={section === 'inbox' ? 'page' : undefined}
              onClick={() => onOpenSection('inbox')}
            >
              Inbox
            </button>
            {/**
             * ⛔ BOOKINGS IS THE THIRD BUILT SECTION AND IT GOES IN THE NAV IN
             * §13's OWN ORDER: Inbox, Bookings, Contacts. The nav still lists
             * only what exists - four of §13's seven are absent, deliberately,
             * because a nav item leading nowhere is the empty-card rule applied
             * to navigation.
             */}
            <button
              type="button"
              className={section === 'bookings' ? 'app-bar__section app-bar__section--on' : 'app-bar__section'}
              aria-current={section === 'bookings' ? 'page' : undefined}
              onClick={() => onOpenSection('bookings')}
              data-testid="nav-bookings"
            >
              Bookings
            </button>
            <button
              type="button"
              className={section === 'contacts' ? 'app-bar__section app-bar__section--on' : 'app-bar__section'}
              aria-current={section === 'contacts' ? 'page' : undefined}
              onClick={() => onOpenSection('contacts')}
              data-testid="nav-contacts"
            >
              Contacts
            </button>
          </nav>
        )}
        <span className="app-bar__user">
          {user.firstName} {user.lastName}
        </span>
        {!user.totpEnabled && (
          <button type="button" className="app-bar__action" onClick={onSetUpTwoFactor}>
            Set Up Two Factor Authentication
          </button>
        )}
        <button
          type="button"
          className="app-bar__action"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            // `signOut` never rejects: it swallows a network failure on purpose,
            // because refusing to clear local state when the network blipped is
            // worse than a cookie outliving its session.
            void signOut().then(() => {
              setBusy(false)
              onSignedOut()
            })
          }}
        >
          {busy ? 'Please Wait' : 'Sign Out'}
        </button>
      </header>

      {/* ⚠️ `currentUserId` IS FOR NOTES ONLY. A note's author is the only
          person who may edit it, so the thread has to know who is reading. The
          service enforces the rule; this decides what to OFFER, and a control
          that would always be refused is worse than no control. */}
      {/**
       * ⛔ ONE SECTION AT A TIME, AND THE SHELL DECIDES WHICH. CRM-BUILD item 31
       * adds the product's second top-level section, so this file stops being
       * "the app bar plus the inbox" and becomes the frame both sit in.
       *
       * ⚠️ IT IS A BRANCH RATHER THAN A NAV YET, and that is honest about what
       * exists: §13's nav has seven sections and two are built. A nav listing
       * five destinations that do not exist is the empty-card rule applied to
       * navigation - it says the feature is there and this deployment has none.
       */}
      {showBoard && onBoardParamsChange && onOpenBooking ? (
        <BookingsSection
          params={new URLSearchParams(boardParams)}
          onParamsChange={onBoardParamsChange}
          onOpenBooking={onOpenBooking}
          /** ⚠️ The LIST's contact column opens the contact record, which the
           *  board's cards never needed. Falls back to a no-op rather than
           *  hiding the column: the name is still readable. */
          onOpenContact={onOpenContact ?? (() => {})}
        />
      ) : showContactsList && onOpenContact && onListParamsChange ? (
        <ContactsList
          onOpenContact={onOpenContact}
          params={new URLSearchParams(listParams)}
          onParamsChange={onListParamsChange}
        />
      ) : contactReference === null ? (
        <InboxShell
          conversationId={conversationId}
          highlightMessageId={highlightMessageId}
          onOpen={onOpenConversation}
          currentUserId={user.id}
        />
      ) : (
        <ContactRecord reference={contactReference} onOpenThread={onOpenConversation} />
      )}
    </div>
  )
}
